import React from 'react';

import i18n from 'util/i18n';
import { DateSummary, Summary } from 'components/Summary';

import { SUBSTANCE_USE, SUBSTANCE_USE_DRUGS_USAGE } from 'config/formSections/substanceUse';
import { formConfig } from 'config/forms';
import { getNumberOfYearsString } from 'helpers/text';

import Subsection from 'components/Section/shared/Subsection';
import connectSubsection from 'components/Section/shared/SubsectionConnector';
import Branch from 'components/Form/Branch';
import Show from 'components/Form/Show';
import Accordion from 'components/Form/Accordion';
import { isFormSF852013 } from 'helpers/form';
import Comments from 'components/Form/Comments';
import { isPVQForm } from 'helpers/form';
import DrugUseItem from './DrugUseItem';

const sectionConfig = {
  key: SUBSTANCE_USE_DRUGS_USAGE.key,
  section: SUBSTANCE_USE.name,
  store: SUBSTANCE_USE.store,
  subsection: SUBSTANCE_USE_DRUGS_USAGE.name,
  storeKey: SUBSTANCE_USE_DRUGS_USAGE.storeKey,
};

export class DrugUses extends Subsection {
  constructor(props) {
    super(props);

    const {
      section, subsection, store, storeKey
    } = sectionConfig;

    this.section = section;
    this.subsection = subsection;
    this.store = store;
    this.storeKey = storeKey;
    this.updateList = this.updateList.bind(this);
    this.updateUsedDrugs = this.updateUsedDrugs.bind(this);
    this.summary = this.summary.bind(this);
  }

  update = (updateValues) => {
    this.props.onUpdate(this.storeKey, {
      UsedDrugs: this.props.UsedDrugs,
      List: this.props.List,
      Comments: this.props.Comments,
      SummaryComments: this.props.SummaryComments,
      ...updateValues,
    });
  };

  updateList = (values) => {
    this.update({
      List: values,
    });
  };

  updateUsedDrugs = (values) => {
    this.update({
      UsedDrugs: values,
      List: values.value === 'Yes' ? this.props.List : {},
      SummaryComments: values.value === 'Yes' ? this.props.SummaryComments : {},
    });
  };

  summary = (item, index) => {
    const o = (item || {}).Item || {};
    const range = isPVQForm(this.props.formType) ? DateSummary({ from: o.FirstUse, to: o.RecentUse }) : null;
    const drug = (o.DrugType || {}).value;

    return Summary({
      type: i18n.t('substance.drugs.use.collection.itemType'),
      index,
      left: drug,
      right: range,
      placeholder: i18n.t('substance.drugs.use.collection.summary'),
    });
  };

  updateComments = (values) => {
    this.update({
      Comments: values,
    });
  };

  updateSummaryComments = (values) => {
    this.update({
      SummaryComments: values,
    });
  };

  render() {
    const {
      formType,
      formVersion,
      requireDrugWhileSafety,
      requireDrugWithClearance,
      requireDrugInFuture,
      errors,
    } = this.props;
    const formTypeConfig = formType && formConfig[formType];
    const years = formTypeConfig && formTypeConfig.SUBSTANCE_DRUG_USE_YEARS;
    const numberOfYearsString = getNumberOfYearsString(years);

    const accordionErrors = errors && errors.filter((e) => e.indexOf('List.accordion') === 0);
    const isPvq = isPVQForm(this.props.formType);

    return isPvq ? (
      <div
        className="section-content drug-uses"
        data-section={SUBSTANCE_USE.key}
        data-subsection={SUBSTANCE_USE_DRUGS_USAGE.key}
      >
        <h1 className="section-header">{i18n.t('substance.pvq.subsection.drugs.usage')}</h1>
        <Branch
          name="UsedDrugs"
          attribute="UsedDrugs"
          label={i18n.t('substance.pvq.drugs.heading.drugUses', { numberOfYearsString })}
          labelSize="h4"
          className="used-drugs"
          {...this.props.UsedDrugs}
          warning
          onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
          required={this.props.required}
          onUpdate={this.updateUsedDrugs}
          scrollIntoView={this.props.scrollIntoView}
        >
        </Branch>

        <Show when={this.props.UsedDrugs.value === 'Yes'}>
          <Accordion
            defaultState={this.props.defaultState}
            {...this.props.List}
            scrollToBottom={this.props.scrollToBottom}
            summary={this.summary}
            onUpdate={this.updateList}
            onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
            errors={accordionErrors}
            description={i18n.t('substance.pvq.drugs.use.collection.description')}
            appendTitle={i18n.t('substance.pvq.drugs.use.collection.appendTitle')}
            appendLabel={i18n.t('substance.pvq.drugs.use.collection.appendLabel')}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
          >
            <DrugUseItem
              name="Item"
              bind
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              requireDrugWhileSafety={requireDrugWhileSafety}
              requireDrugWithClearance={requireDrugWithClearance}
              requireDrugInFuture={requireDrugInFuture}
              formType={formType}
              formVersion={formVersion}
            />
          </Accordion>
        </Show>
      </div>
    ) : (
      <div
        className="section-content drug-uses"
        data-section={SUBSTANCE_USE.key}
        data-subsection={SUBSTANCE_USE_DRUGS_USAGE.key}
      >
        <h1 className="section-header">{i18n.t('substance.subsection.drugs.usage')}</h1>
        {i18n.m('substance.drugs.para.drugUses')}
        <Branch
          name="UsedDrugs"
          attribute="UsedDrugs"
          label={i18n.t('substance.drugs.heading.drugUses', { numberOfYearsString })}
          labelSize="h4"
          className="used-drugs"
          {...this.props.UsedDrugs}
          warning
          onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
          required={this.props.required}
          onUpdate={this.updateUsedDrugs}
          scrollIntoView={this.props.scrollIntoView}
        >
          {i18n.t('substance.drugs.use.para.drugUses')}
        </Branch>
        {!isFormSF852013(formType, formVersion) && (
          <Comments
            title={i18n.t('substance.comments.optional')}
            name="Comments"
            className="optional-comment"
            attribute="Comments"
            {...this.props.Comments}
            onUpdate={this.updateComments}
            required={false}
          />
        )}

        <Show when={this.props.UsedDrugs.value === 'Yes'}>
          <Accordion
            defaultState={this.props.defaultState}
            {...this.props.List}
            scrollToBottom={this.props.scrollToBottom}
            summary={this.summary}
            onUpdate={this.updateList}
            onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
            errors={accordionErrors}
            description={i18n.t('substance.drugs.use.collection.description')}
            appendTitle={i18n.t('substance.drugs.use.collection.appendTitle')}
            appendLabel={i18n.t('substance.drugs.use.collection.appendLabel')}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
          >
            <DrugUseItem
              name="Item"
              bind
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              requireDrugWhileSafety={requireDrugWhileSafety}
              requireDrugWithClearance={requireDrugWithClearance}
              requireDrugInFuture={requireDrugInFuture}
              formType={formType}
              formVersion={formVersion}
            />
          </Accordion>
          {!isFormSF852013(formType, formVersion) && (
            <Comments
              title={i18n.t('substance.comments.summary')}
              name="SummaryComments"
              className="summary-comment"
              attribute="SummaryComments"
              {...this.props.SummaryComments}
              onUpdate={this.updateSummaryComments}
              required={false}
            />
          )}
        </Show>
      </div>
    );
  }
}

DrugUses.defaultProps = {
  UsedDrugs: {},
  List: {},
  Comments: {},
  SummaryComments: {},
  onError: (value, arr) => arr,
  section: 'substance',
  subsection: 'drugs/usage',
  dispatch: () => { },
  scrollToBottom: '',
  errors: [],
};

export default connectSubsection(DrugUses, sectionConfig);
